import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import classNames from 'classnames';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalTechnology from '/@/components/crop-monitoring/modal-technology';
import { _ } from '/@/locale';

import { reverse } from '/@/utils/router';
import colors from '../../theme-provider/colors';

import styles from './styles.module.css';

const CPLinesMobile = ({ steps, activeStep, height, width }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'absolute',
                height: `${height - 8}px`,
                width: `${width + 30}px`,
                left: '-30px',
                pointerEvents: 'none',
                pt: 2.5,
                pb: 2,
            }}
        >
            {steps.map((step, index) => (
                <Fragment>
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                height: '0%',
                                width: '100%',
                            },
                            !step.empty && {
                                borderBottom: `1px dashed ${colors.cropMonitoring.grayLine}`,
                                marginBottom: '-0.5px',
                            },
                            index === steps.length - 1 && {
                                width: '0px',
                            },
                            index + 1 === activeStep && {
                                borderBottom: (t) => `2px dashed ${t.palette.warning.main}`,
                                marginBottom: '-1px',
                            },
                        ]}
                    />
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                width: '100%',
                                height: '100%',
                                zIndex: 1,
                            },
                            index === steps.length - 1 && {
                                height: '0px',
                            },
                            index + 1 >= activeStep && {
                                background: '#ffffff78',
                            },
                        ]}
                    />
                </Fragment>
            ))}
        </Box>
    );
};

const CropProtectionProductDetail = ({ product, open, setOpen, state }) => {
    const history = useNavigate();
    const handleClose = () => {
        setOpen(false);
    };

    const handleClick = () => {
        history(
            reverse('catalog.ProductDetail', {
                params: {
                    type: 'crop_protection',
                    itemId: product.id,
                },
            })
        );
    };

    if (!open) return null;
    return (
        <Modal open={open} onClose={handleClose}>
            {product && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 325,
                        bgcolor: '#fff',
                        borderRadius: 2,
                        p: 3,
                    }}
                >
                    <IconButton onClick={handleClose} sx={{ position: 'absolute', right: '10px', top: '10px' }}>
                        <CloseIcon />
                    </IconButton>
                    <ModalTechnology
                        image={product.image}
                        pills={{ type: product.type, crops: product.crops }}
                        name={product.name}
                        description={product.shortDescription}
                        btnText={_('Ver más')}
                        state={state}
                        click={handleClick}
                    />
                </Box>
            )}
        </Modal>
    );
};

const ProgressBarTableMobile = ({
    steps,
    activeStep,
    productsData,
    stageNames,
    modalOpened,
    modalProduct,
    modalState,
    setModalOpened,
    openModal,
}) => {
    const CPTableEl = useRef(null);
    const [CPTableHeight, setCPTableHeight] = useState(0);
    const [CPTableWidth, setCPTableWidth] = useState(0);

    useEffect(() => {
        if (CPTableEl.current) {
            setCPTableHeight(CPTableEl.current.clientHeight + 10);
            setCPTableWidth(CPTableEl.current.scrollWidth + 10);
        }
    }, [productsData]);

    return (
        <Box sx={{ position: 'relative', overflow: 'scroll' }}>
            <CPLinesMobile steps={steps} activeStep={activeStep} height={CPTableHeight} width={CPTableWidth} />
            <Box sx={{ display: 'flex' }} ref={CPTableEl}>
                {productsData.map((col, colIndex) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            my: 2,
                            mx: 2,
                        }}
                        key={colIndex}
                    >
                        {col
                            .reduce((col, row, index) => {
                                let lastItem;
                                if (col.length > 0) {
                                    lastItem = col[col.length - 1];
                                }

                                if (lastItem && row.product.id === lastItem.product.id) {
                                    lastItem.rows = lastItem.rows + 1;
                                    lastItem.lastStage = index;
                                } else {
                                    col.push({
                                        rows: 1,
                                        startStage: index,
                                        product: row.product,
                                    });
                                }

                                return col;
                            }, [])
                            .map(({ rows, product, startStage, lastStage }, i) => {
                                if (product.id === undefined)
                                    return (
                                        <Box
                                            item
                                            sx={{ p: 0.25, width: '150px', height: `${(rows * (2591 - 37)) / 14}px` }}
                                            key={i}
                                        />
                                    );
                                return (
                                    <Box
                                        sx={{
                                            pb: 2,
                                            pt: 1,
                                            width: '150px',
                                            height: `${(rows * (2591 - 40)) / 14}px`,
                                        }}
                                        key={i}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                p: 0.25,
                                                color: 'white',
                                                height: '100%',
                                                borderRadius: '5px',
                                            }}
                                            onClick={() => openModal(product, startStage, rows)}
                                            className={classNames(
                                                styles['type-default'],
                                                styles[`type-${product.type.en.toLowerCase()}`]
                                            )}
                                        >
                                            <Box
                                                sx={[
                                                    {
                                                        display: 'flex',
                                                        alignContent: 'center',
                                                        placeSelf: 'center',
                                                        justifyContent: 'center',
                                                        p: 1,
                                                        width: '90%',
                                                    },
                                                ]}
                                            >
                                                <img
                                                    style={{ maxWidth: '100%', maxHeight: '72px' }}
                                                    src={product.image}
                                                    alt={product.name}
                                                />
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    color: 'white',
                                                    p: 1,
                                                    mt: 'auto',
                                                }}
                                            >
                                                <Box sx={{ fontWeight: 'bold', fontSize: '0.7em', mb: 0.5 }}>
                                                    {product.name.toUpperCase()}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: '0.8em',
                                                        background: 'white',
                                                        p: 1,
                                                        px: 1.5,
                                                        width: 'fit-content',
                                                        borderRadius: '5px',
                                                    }}
                                                    className={classNames(
                                                        styles['type-default'],
                                                        styles[`color-type-${product.type.en.toLowerCase()}`]
                                                    )}
                                                >
                                                    {rows === 1 && `${stageNames[startStage]}`}
                                                    {rows > 1 && `${stageNames[startStage]}-${stageNames[lastStage]}`}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                    </Box>
                ))}
            </Box>
            <CropProtectionProductDetail
                open={modalOpened}
                setOpen={setModalOpened}
                product={modalProduct}
                state={modalState}
            />
        </Box>
    );
};

export default ProgressBarTableMobile;
